import {
  ShippingCostConfig,
  ShippingCostResponsibility,
} from 'types/incentive';

export type SortableShippingOption = {
  id: string;
  sortOrder?: number | null;
};

const getSortableSortOrder = (option: SortableShippingOption) =>
  typeof option.sortOrder === 'number' && Number.isFinite(option.sortOrder)
    ? option.sortOrder
    : Number.POSITIVE_INFINITY;

export const getOrderedShippingOptions = <T extends SortableShippingOption>(
  options: T[] | null | undefined = [],
) =>
  (options ?? [])
    .map((option, index) => ({ index, option }))
    .sort((left, right) => {
      const sortOrderDifference =
        getSortableSortOrder(left.option) - getSortableSortOrder(right.option);

      return sortOrderDifference || left.index - right.index;
    })
    .map(({ option }, index) => ({
      ...option,
      sortOrder: index + 1,
    }));

export const getReorderedShippingOptions = <T extends SortableShippingOption>(
  options: T[],
  draggedId: string,
  targetId: string,
) => {
  const orderedOptions = getOrderedShippingOptions(options);
  const draggedIndex = orderedOptions.findIndex(
    (option) => option.id === draggedId,
  );
  const targetIndex = orderedOptions.findIndex(
    (option) => option.id === targetId,
  );

  if (draggedIndex < 0 || targetIndex < 0 || draggedIndex === targetIndex) {
    return orderedOptions;
  }

  const nextOptions = orderedOptions.slice();
  const [draggedOption] = nextOptions.splice(draggedIndex, 1);
  nextOptions.splice(targetIndex, 0, draggedOption);

  return nextOptions.map((option, index) => ({
    ...option,
    sortOrder: index + 1,
  }));
};

export const isShippingOptionAvailable = (option: {
  enabled?: boolean | null;
  customerVisible?: boolean | null;
}) => Boolean(option.enabled && option.customerVisible);

type ShippingSettingsValidationInput = {
  defaultWeightOz: string;
  defaultLengthIn: string;
  defaultWidthIn: string;
  defaultHeightIn: string;
  protectedOrdersShippingCost: ShippingCostValidationConfig;
  unprotectedOrdersShippingCost: ShippingCostValidationConfig;
};

type ShippingCostValidationConfig = Omit<
  ShippingCostConfig,
  'flatFeeAmount'
> & {
  flatFeeAmount?: number | string | null;
};

const getOptionalPositiveNumberError = (value: string, label: string) => {
  if (!value.trim()) return null;
  const parsed = Number(value);

  return Number.isFinite(parsed) && parsed > 0
    ? null
    : `${label} must be greater than 0.`;
};

const getFlatFeeError = (
  config: ShippingCostValidationConfig,
  label: string,
) => {
  if (
    config.responsibility !== ShippingCostResponsibility.CUSTOMER_PAYS_FLAT_FEE
  ) {
    return null;
  }

  const parsedFee =
    config.flatFeeAmount === null ||
    (typeof config.flatFeeAmount === 'string' && !config.flatFeeAmount.trim())
      ? Number.NaN
      : Number(config.flatFeeAmount);
  if (!Number.isFinite(parsedFee) || parsedFee < 0) {
    return `${label} flat label fee must be 0 or greater.`;
  }
  const currencyFees = config.flatFeeAmountsByCurrency;
  if (currencyFees === undefined) return null;
  if (
    !currencyFees ||
    typeof currencyFees !== 'object' ||
    Array.isArray(currencyFees)
  ) {
    return `${label} currency fees must be a map of currency codes and amounts.`;
  }
  const seen = new Set<string>();
  for (const [code, amount] of Object.entries(currencyFees)) {
    const currency = code.trim().toUpperCase();
    if (!/^[A-Z]{3}$/.test(currency)) {
      return `${label} ${currency} is not a valid three-letter currency code.`;
    }
    if (seen.has(currency)) {
      return `${label} currency fees must be unique. ${currency} is already configured.`;
    }
    if (typeof amount !== 'number' || !Number.isFinite(amount) || amount < 0) {
      return `${label} ${currency} flat label fee must be 0 or greater.`;
    }
    seen.add(currency);
  }
  return null;
};

export const getShippingSettingsValidationError = ({
  defaultWeightOz,
  defaultLengthIn,
  defaultWidthIn,
  defaultHeightIn,
  protectedOrdersShippingCost,
  unprotectedOrdersShippingCost,
}: ShippingSettingsValidationInput): string | null =>
  getOptionalPositiveNumberError(defaultWeightOz, 'Package weight') ||
  getOptionalPositiveNumberError(defaultLengthIn, 'Package length') ||
  getOptionalPositiveNumberError(defaultWidthIn, 'Package width') ||
  getOptionalPositiveNumberError(defaultHeightIn, 'Package height') ||
  getFlatFeeError(protectedOrdersShippingCost, 'Guaranteed orders') ||
  getFlatFeeError(unprotectedOrdersShippingCost, 'Standard orders');
